import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessagePopup } from "./MessagePopup";
import { FaArrowLeft } from "react-icons/fa";

interface Message {
  id: number;
  sender: string;
  title: string;
  description: string;
  timestamp: string;
  avatar?: string;
  isRead?: boolean;
  role?: string;
  fullMessage?: string;
}

const mockMessages: Message[] = [
  {
    id: 1,
    sender: "Adeyemi Ojo",
    title: "New Compliance Guidelines",
    description: "Please review the updated guideline for product sanitization",
    timestamp: "5m ago",
    role: "System Administrator",
    fullMessage:
      "Hello Kehinde,\n\nI hope this message finds you well. There have been some important updates to our product sanitization guidelines that I need you to review. Please take some time to go through it carefully, as these changes will impact your team. Let me know if you have any questions.",
    isRead: false,
  },
  {
    id: 2,
    sender: "Jasmine Lee",
    title: "Quarterly Sales Report",
    description:
      "Attached is the report detailing sales performance over the quarter",
    timestamp: "5m ago",
    role: "Sales Manager",
    fullMessage:
      "Hello,\n\nPlease find attached the quarterly sales report with detailed metrics.",
    isRead: false,
  },
  {
    id: 3,
    sender: "Adeyemi Ojo",
    title: "New Compliance Guidelines",
    description: "Please review the updated guideline for product sanitization",
    timestamp: "5m ago",
    role: "System Administrator",
    fullMessage:
      "Hello Kehinde,\n\nI hope this message finds you well. There have been some important updates...",
    isRead: true,
  },
  {
    id: 4,
    sender: "Michael Chen",
    title: "Product Launch Update",
    description: "Exciting new features will be available in the next release",
    timestamp: "5m ago",
    role: "Product Manager",
    fullMessage:
      "Hello,\n\nI'm excited to share that we have some great new features coming in the next release...",
    isRead: true,
  },
  {
    id: 5,
    sender: "Sarah Johnson",
    title: "Q4 Sales Report",
    description:
      "The sales report for Q4 is now available for review. Key insights included",
    timestamp: "5m ago",
    role: "Sales Director",
    fullMessage:
      "Hello,\n\nPlease review the attached Q4 sales report with key insights...",
    isRead: true,
  },
];

export default function MessagesCard(props?: { onClose?: () => void }) {
  const navigate = useNavigate();
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleSelectMessage = (message: Message) => {
    setSelectedMessage(message);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedMessage(null);
  };

  const handleBackToInbox = () => {
    setIsModalOpen(false);
    setSelectedMessage(null);
  };

  const handleMarkAllAsRead = () => {
    console.log("Marking all as read");
  };

  return (
    <>
      {/* Messages List View */}
      {!isModalOpen && (
        <div className="w-72 bg-white rounded-lg shadow-lg p-4 border border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-base font-semibold text-gray-900">Messages</h2>
            <button
              onClick={handleMarkAllAsRead}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
            >
              Mark all as read
            </button>
          </div>

          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {mockMessages.map((message) => (
              <div
                key={message.id}
                onClick={() => handleSelectMessage(message)}
                className={`flex gap-3 p-3 rounded-lg cursor-pointer transition ${
                  message.isRead
                    ? "hover:bg-gray-50 bg-gray-50"
                    : "hover:bg-blue-50 bg-white border border-gray-200"
                }`}
              >
                <div className="relative flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold text-sm">
                    {message.sender.charAt(0)}
                  </div>
                  {!message.isRead && (
                    <div className="absolute top-0 right-0 w-3 h-3 bg-red-500 rounded-full border-2 border-white"></div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <p
                        className={`text-sm ${
                          message.isRead
                            ? "font-normal text-gray-600"
                            : "font-semibold text-gray-900"
                        }`}
                      >
                        {message.sender}
                      </p>
                      <p
                        className={`text-xs ${
                          message.isRead
                            ? "text-gray-500"
                            : "text-gray-700 font-medium"
                        }`}
                      >
                        {message.title}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 truncate mt-1">
                    {message.description}
                  </p>
                  <span className="text-xs text-gray-400 mt-1 block">
                    {message.timestamp}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={() => {
              navigate("/dashboard/messages");
              props?.onClose?.();
            }}
            className="w-full mt-4 py-2 text-xs text-blue-600 hover:bg-blue-50 rounded-lg transition font-medium"
          >
            View all messages →
          </button>
        </div>
      )}

      {/* Message Detail Modal */}
      {isModalOpen && selectedMessage && (
        <div className="w-72 bg-white rounded-lg shadow-lg p-4 border border-gray-200">
          {/* Header with back button */}
          <div className="flex items-center gap-2 mb-4">
            <button
              onClick={handleBackToInbox}
              className="text-blue-600 hover:text-blue-800 transition"
            >
              <FaArrowLeft size={16} />
            </button>
            <span className="text-sm text-gray-600">Back to inbox</span>
          </div>

          {/* Sender Info */}
          <div className="flex gap-3 mb-4 pb-4 border-b border-gray-200">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
              {selectedMessage.sender.charAt(0)}
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">
                {selectedMessage.sender}
              </p>
              <p className="text-xs text-gray-500">{selectedMessage.role}</p>
              <p className="text-xs text-gray-400 mt-1">
                {selectedMessage.timestamp}
              </p>
            </div>
          </div>

          {/* Message Title */}
          <h3 className="text-sm font-semibold text-gray-900 mb-3">
            {selectedMessage.title}
          </h3>

          {/* Message Body */}
          <p className="text-sm text-gray-700 leading-relaxed mb-4 max-h-[200px] overflow-y-auto">
            {selectedMessage.fullMessage}
          </p>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={handleBackToInbox}
              className="flex-1 py-2 px-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition text-sm font-medium"
            >
              Close
            </button>
            <button className="flex-1 py-2 px-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium">
              Reply
            </button>
          </div>
        </div>
      )}

      {selectedMessage && (
        <MessagePopup
          isOpen={isModalOpen}
          message={selectedMessage}
          onClose={handleCloseModal}
        />
      )}
    </>
  );
}
